import { DestroyRef, inject, Injectable, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  TranscodeMonitorResult,
  TranscodeMonitorService,
  TranscodeMonitorType,
} from '../../../services/transcode-monitor.service';
import { ProjectService } from '../../../services/project.service';
import { AnnotationPluginEventService } from './annotation-plugin-event.service';
import { EmitterService } from '../../../services/emitter.service';
import { FileTranscodedEvent } from '../../../models';
import { SimpleFilePage, SimpleFilePages } from '../../../interfaces';
import { MediaPlayerFile } from '../../../interfaces/media-player-store.interface';
import { MediaPlayerStoreService, MediaPlayerStore } from '../../../services/media-player-store.service';
import { FileUtilsService } from '../../../services/file-utils.service';
import { FileTranscodingStatus, MediaType } from '../../../models/media-player-types';
import { FileTranscodeInfo } from '../../../interfaces/file-tree.interface';
import { LogService } from '../../../services/log.service';

@Injectable()
export class FileTranscodeService implements OnDestroy {
  private readonly mpStoreService: MediaPlayerStoreService = inject(MediaPlayerStoreService);
  private readonly transcodeMonitorService: TranscodeMonitorService = inject(TranscodeMonitorService);
  private readonly projectService: ProjectService = inject(ProjectService);
  private readonly annotationPluginService: AnnotationPluginEventService = inject(AnnotationPluginEventService);
  private readonly destroyRef: DestroyRef = inject(DestroyRef);
  private readonly fileUtilsService: FileUtilsService = inject(FileUtilsService);
  private readonly transcodingFailedTimeout: number = 30 * 60 * 1000;
  private readonly fallbackTimeout: number = 24 * 60 * 1000;
  private transcodeSubscription: Record<string, Subscription> = {};
  private fileIdToMonitor: string = '';
  private readonly logService: LogService = inject(LogService);

  ngOnDestroy(): void {
    Object.values(this.transcodeSubscription).forEach((sub: Subscription) => sub.unsubscribe());
    this.transcodeSubscription = {};

    if (this.fileIdToMonitor) {
      this.transcodeMonitorService.stopMonitoringFile(this.fileIdToMonitor);
    }
  }

  public checkFileTranscoding(
    item: MediaPlayerFile,
    mediaType: string,
    enablePage?: boolean,
    filePages?: SimpleFilePages | null,
    instanceId?: string
  ): void {
    if (!instanceId) return;
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store) return;

    const codec: string = this.determineCodec(mediaType, item, enablePage);
    this.checkCurrentTranscodingStatus(item, codec, enablePage, filePages, instanceId);

    if (!codec) {
      return;
    }

    if (store.fileTranscodingStatusSignal() === FileTranscodingStatus.FAILED && !!item.replacementId) {
      this.checkReplacementFile(item.replacementId, item.id, codec, instanceId);
      return;
    }

    if (
      store.fileTranscodingStatusSignal() === FileTranscodingStatus.IN_PROGRESS &&
      !(item.id in this.transcodeSubscription)
    ) {
      this.startFileMonitoring(item, codec, store);
    }
  }

  /**
   * Determine codec based on media type
   */
  private determineCodec(mediaType: string, item: MediaPlayerFile, enablePage?: boolean): string {
    if (mediaType === MediaType.CONVERTIBLEIMAGE) {
      return this.getConvertibleImageCodec(item);
    }
    if (mediaType === MediaType.OFFICEDOC) {
      return 'pdf';
    }
    if (mediaType === MediaType.POWERPOINT || mediaType === MediaType.KEYNOTE) {
      return enablePage ? 'images' : 'pdf';
    }
    if (mediaType === MediaType.PDF) {
      return 'images';
    }
    return '';
  }

  /**
   * Get codec for convertible image (png or jpg)
   */
  private getConvertibleImageCodec(item: MediaPlayerFile): string {
    const pngMetadata: FileTranscodeInfo = item?.transcodeMetadata?.find((m: FileTranscodeInfo) => m.codec === 'png');
    const jpgMetadata: FileTranscodeInfo = item?.transcodeMetadata?.find((m: FileTranscodeInfo) => m.codec === 'jpg');

    if (pngMetadata) {
      return 'png';
    }
    if (jpgMetadata) {
      return 'jpg';
    }
    return 'png';
  }

  /**
   * Start monitoring file transcoding
   */
  private startFileMonitoring(item: MediaPlayerFile, codec: string, store: MediaPlayerStore): void {
    this.switchFileToMonitor(item.id);
    const obs$: any = this.transcodeMonitorService.monitorFile({
      projectId: store.projectIdSignal() ?? this.projectService.project?.id,
      fileId: item.id,
      codec: codec,
      type: codec === 'images' ? TranscodeMonitorType.Pages : TranscodeMonitorType.SingleFile,
    });
    this.transcodeSubscription[item.id] = obs$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: TranscodeMonitorResult) => this.handleTranscodeMonitorResult(data, codec, store.instanceIdSignal()),
    });
  }

  /**
   * Handle transcode monitor result
   */
  private handleTranscodeMonitorResult(data: TranscodeMonitorResult, codec: string, instanceId: string): void {
    if (!data?.file) {
      return;
    }

    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store) return;

    const isSameFile: boolean = data.fileId === store.itemSignal()?.id;

    if (isSameFile && store.itemSignal()) {
      this.fileUtilsService.copyFilePropsAfterScanning(data.file, store.itemSignal());
      // Update store with the modified item to trigger signal updates
      store.updateStore({ item: { ...store.itemSignal() } });
    }

    if (data.isSuccess) {
      EmitterService.get(FileTranscodedEvent).emit(data);
    }

    if ((codec === 'images' || codec === 'pdf') && data.isSuccess) {
      this.handlePdfOrImagesSuccess(data, store);
    }

    if (isSameFile) {
      store.updateStore({
        fileTranscodingStatus: data.isSuccess ? FileTranscodingStatus.SUCCESS : FileTranscodingStatus.FAILED,
        isLoading: false,
      });
    }
  }

  /**
   * Handle success for pdf or images codec
   */
  private handlePdfOrImagesSuccess(data: TranscodeMonitorResult, store: MediaPlayerStore): void {
    if (data.fileId === store.itemSignal()?.id && store.itemSignal()) {
      this.setPdfPreviewAndInitializeComments(store);
    }
  }

  private checkCurrentTranscodingStatus(
    item: MediaPlayerFile,
    codec: string,
    enablePage?: boolean,
    filePages?: SimpleFilePages | null,
    instanceId?: string
  ): void {
    if (!instanceId) return;
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store) return;

    store.updateStore({ useFallbackDisplay: false });
    const elapsedTime: number = Date.now() - item.timeCreated;
    const metadata: FileTranscodeInfo = item?.transcodeMetadata?.find((m: FileTranscodeInfo) => m.codec === codec);
    const isTimeout: boolean = this.processMetadataStatus(
      metadata,
      codec,
      enablePage,
      filePages,
      elapsedTime,
      instanceId
    );

    this.handleFailedImagesTranscoding(codec, isTimeout, elapsedTime, item, instanceId);
  }

  /**
   * Process metadata status and return if timeout occurred
   */
  private processMetadataStatus(
    metadata: FileTranscodeInfo,
    codec: string,
    enablePage: boolean | undefined,
    filePages: SimpleFilePages | null | undefined,
    elapsedTime: number,
    instanceId: string
  ): boolean {
    if (!metadata) {
      return this.handleNoMetadata(elapsedTime, instanceId);
    }

    return this.handleMetadataResolution(metadata, codec, enablePage, filePages, elapsedTime, instanceId);
  }

  /**
   * Handle case when no metadata exists
   */
  private handleNoMetadata(elapsedTime: number, instanceId: string): boolean {
    return this.handleTimeoutBasedStatus(elapsedTime, instanceId);
  }

  /**
   * Handle metadata resolution status
   */
  private handleMetadataResolution(
    metadata: FileTranscodeInfo,
    codec: string,
    enablePage: boolean | undefined,
    filePages: SimpleFilePages | null | undefined,
    elapsedTime: number,
    instanceId: string
  ): boolean {
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store) return false;

    switch (metadata?.resolution) {
      case 'true':
        this.handleResolutionTrue(codec, enablePage, filePages, instanceId);
        return false;
      case 'false':
        store.updateStore({ fileTranscodingStatus: FileTranscodingStatus.FAILED });
        return false;
      default:
        return this.handleTimeoutBasedStatus(elapsedTime, instanceId);
    }
  }

  /**
   * Handle resolution 'true' case
   */
  private handleResolutionTrue(
    codec: string,
    enablePage: boolean | undefined,
    filePages: SimpleFilePages | null | undefined,
    instanceId: string
  ): void {
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store) return;

    if (enablePage && codec == 'images') {
      this.handleImagesWithPages(filePages, instanceId);
    } else {
      store.updateStore({ fileTranscodingStatus: FileTranscodingStatus.SUCCESS });
    }
  }

  /**
   * Handle images with pages
   */
  private handleImagesWithPages(filePages: SimpleFilePages | null | undefined, instanceId: string): void {
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store) return;

    if (filePages) {
      const ready: boolean = filePages.pages?.every((c: SimpleFilePage) => c.status === 'READY');
      if (ready) {
        store.updateStore({ isLoading: false, fileTranscodingStatus: FileTranscodingStatus.SUCCESS });
      } else {
        store.updateStore({ isLoading: true, fileTranscodingStatus: FileTranscodingStatus.IN_PROGRESS });
      }
    } else {
      store.updateStore({ isLoading: true, fileTranscodingStatus: FileTranscodingStatus.IN_PROGRESS });
    }
  }

  /**
   * Handle status based on timeout
   */
  private handleTimeoutBasedStatus(elapsedTime: number, instanceId: string): boolean {
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store) return false;

    if (elapsedTime > this.transcodingFailedTimeout) {
      store.updateStore({ fileTranscodingStatus: FileTranscodingStatus.FAILED });
      return true;
    }
    store.updateStore({ fileTranscodingStatus: FileTranscodingStatus.IN_PROGRESS });
    return false;
  }

  /**
   * Handle failed images transcoding with fallback check
   */
  private handleFailedImagesTranscoding(
    codec: string,
    isTimeout: boolean,
    elapsedTime: number,
    item: MediaPlayerFile,
    instanceId: string
  ): void {
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store) return;

    if (store.fileTranscodingStatusSignal() !== FileTranscodingStatus.FAILED || codec !== 'images') {
      return;
    }

    if (isTimeout) {
      if (elapsedTime > this.fallbackTimeout) {
        this.checkFallbackDisplay(item, instanceId);
      }
    } else {
      this.checkFallbackDisplay(item, instanceId);
    }
  }

  private checkReplacementFile(fileId: string, replacementFileId: string, codec: string, instanceId: string): void {
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store) return;

    this.switchFileToMonitor(fileId);
    const obs$: any = this.transcodeMonitorService.monitorReplacementFile({
      fileId,
      projectId: store.projectIdSignal() ?? this.projectService.project?.id,
      replacementFileId,
      codec,
      type: codec === 'images' ? TranscodeMonitorType.PagesReplacement : TranscodeMonitorType.SingleFileReplacement,
    });

    this.transcodeSubscription[fileId] = obs$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: TranscodeMonitorResult | boolean) => this.handleReplacementFileResult(data, codec, instanceId),
    });
  }

  /**
   * Handle replacement file monitor result
   */
  private handleReplacementFileResult(data: TranscodeMonitorResult | boolean, codec: string, instanceId: string): void {
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store) return;

    if (typeof data === 'boolean') {
      store.updateStore({ fileTranscodingStatus: data ? FileTranscodingStatus.SUCCESS : FileTranscodingStatus.FAILED });
      return;
    }

    const isSameFile: boolean = data.fileId === store.itemSignal()?.id;

    if (isSameFile) {
      store.updateStore({
        isLoading: false,
        fileTranscodingStatus: data.isSuccess ? FileTranscodingStatus.SUCCESS : FileTranscodingStatus.FAILED,
      });

      if (data.file && store.itemSignal()) {
        this.fileUtilsService.copyFilePropsAfterScanning(data.file, store.itemSignal());
        // Update store with the modified item to trigger signal updates
        store.updateStore({ item: { ...store.itemSignal() } });
      }
    }

    if (data.isSuccess) {
      EmitterService.get(FileTranscodedEvent).emit(data);
    }

    if (isSameFile && data.isSuccess && store.itemSignal()) {
      if (codec === 'images' || codec === 'pdf') {
        this.setPdfPreviewAndInitializeComments(store);
      }
    }
  }

  /**
   * Set PDF preview and initialize comments
   */
  private setPdfPreviewAndInitializeComments(store: MediaPlayerStore): void {
    if (!store.itemSignal()) {
      return;
    }

    // Ensure signedURL exists before setting previewSRC
    const { signedURL } = store.itemSignal();
    if (!signedURL) {
      this.logService.logMessage('warning', 'Cannot set PDF previewSRC: signedURL is missing');
      return;
    }

    store.updateStore({
      item: { ...store.itemSignal(), previewSRC: signedURL + '?thumbnail=_PDF' },
      mediaType: 'pdf',
    });
    this.annotationPluginService.initializeComments('pdf', store.instanceIdSignal());
  }

  private checkFallbackDisplay(item: MediaPlayerFile, instanceId: string): void {
    const store: MediaPlayerStore | null = this.mpStoreService.getStore(instanceId);
    if (!store) return;

    if (item?.contentType == 'application/pdf') {
      store.updateStore({ fileTranscodingStatus: FileTranscodingStatus.SUCCESS, useFallbackDisplay: true });
    } else {
      const pdfMetadata: FileTranscodeInfo = item?.transcodeMetadata?.find((m: FileTranscodeInfo) => m.codec == 'pdf');
      if (pdfMetadata?.resolution == 'true') {
        store.updateStore({ fileTranscodingStatus: FileTranscodingStatus.SUCCESS, useFallbackDisplay: true });
      }
    }
  }

  private switchFileToMonitor(fileId: string): void {
    if (this.fileIdToMonitor == fileId) {
      return;
    }

    if (this.fileIdToMonitor) {
      this.transcodeMonitorService.stopMonitoringFile(this.fileIdToMonitor);
    }

    this.fileIdToMonitor = fileId;
  }
}
